import React, { useContext } from "react";

import { TimekeepingContext } from "../../../../context/domain/timekeeping/TimekeepingContext";

import { cancelClockIn } from "../../../../services/api/timekeeping";

import { Button } from "../../../../components/ui";

/**
 * CancelClockIn Component
 *
 * Provides functionality to cancel a clock-in operation.
 *
 * Features:
 * - Allows users to cancel their current clock-in status
 * - Integrates with the TimekeepingContext to manage state
 * - Displayed only when a user is clocked in but hasn't completed their shift
 *
 * Context Usage:
 * - TimekeepingContext: Manages the clock-in state and provides methods to update it
 *
 * @param {Object} props Component props.
 * @param {string} [props.className] Extra button classes.
 * @returns {JSX.Element} A button that allows users to cancel their clock-in status
 */
const CancelClockIn = ({ className }) => {
  const { setShowClockOutContent, isLoading, setIsLoading } =
    useContext(TimekeepingContext);

  /**
   * Delete the current clock-in event, hide clock-out content, and reload the page.
   *
   * @returns {Promise<void>}
   */
  const handleCancelClockIn = async () => {
    setIsLoading(true);
    // Call the API to cancel clock-in (delete event)
    await cancelClockIn();    // Reset shift data to empty array
    setTimeout(() => {
      setShowClockOutContent(false);
      setIsLoading(false);
      // reload page to reflect changes
      window.location.reload();
    }, 1000); // Optional delay for UX
  };

  return (
    <Button
      onClick={handleCancelClockIn}
      variant={"custom"}
      text={isLoading ? "" : "Cancel Clock In"}
      icon={isLoading ? "spinner" : null}
      className={`py-1 px-6 mt-2 w-full bg-gray-200 hover:bg-gray-300 text-gray-900 ${className} ${isLoading ? "cursor-not-allowed" : ""}`}
    />
  );
};

export default CancelClockIn;
